// Trænerens hånd: viser med en finger, hvor man skal trække eller trykke, og en pulserende ring
// om målet. Ren DOM over kortet, så den er skarp og aldrig fanger tryk. Under onboarding siger den
// ét ord i en lille boble ("Træk!", "Tryk!"), aldrig en tekstvæg, og jubler kort, når det lykkes.

import type { Rect } from '../render/camera';
import { h } from './dom';
import { BRAND } from '../brand';

export interface P {
  x: number;
  y: number;
}

/** Håndens størrelse (px) og fingerspidsen i den (fra øverste venstre hjørne). */
const HAND_W = 48;
const HAND_H = 72;
const TIP: P = { x: 16, y: 2 };

/**
 * Hvor fingerspidsen står i forhold til målet, og hvor meget hånden er drejet (grader), så fingeren
 * peger ind på målet. Først skråt under til højre, og dækker hånden så et bynavn: til venstre, over
 * eller begge. Playtest runde 2: før stod hele hånden 28/24 px ved siden af, så fingeren pegede på
 * tomt hav ved siden af byen.
 */
export interface HandSpot extends P {
  r: number;
}

export const HAND_SPOTS: readonly HandSpot[] = [
  { x: 12, y: 14, r: -28 },
  { x: -12, y: 14, r: 28 },
  { x: 12, y: -14, r: -152 },
  { x: -12, y: -14, r: 152 },
  // Lodret over eller under målet: når byens navn står lige ved siden af, og et skråt greb rammer det.
  { x: 0, y: -18, r: 180 },
  { x: 0, y: 18, r: 0 },
];

/**
 * Håndens kasse på skærmen, når fingeren peger på `at` fra `o`: den drejede hånds yderkasse, som
 * browseren selv måler den. Før var det den udrejede 48x72-kasse, og drejet stak hånden op til
 * 28 px ud over den og ind over et bynavn.
 */
export function handBox(at: P, o: HandSpot): Rect {
  const a = (o.r * Math.PI) / 180;
  const c = Math.cos(a);
  const s = Math.sin(a);
  let x0 = Infinity;
  let y0 = Infinity;
  let x1 = -Infinity;
  let y1 = -Infinity;
  // Hjørnerne i forhold til fingerspidsen, som hånden drejer om.
  for (const [cx, cy] of [[-TIP.x, -TIP.y], [HAND_W - TIP.x, -TIP.y], [-TIP.x, HAND_H - TIP.y], [HAND_W - TIP.x, HAND_H - TIP.y]] as const) {
    const x = cx * c - cy * s;
    const y = cx * s + cy * c;
    x0 = Math.min(x0, x);
    y0 = Math.min(y0, y);
    x1 = Math.max(x1, x);
    y1 = Math.max(y1, y);
  }
  return { x: at.x + o.x + x0, y: at.y + o.y + y0, w: x1 - x0, h: y1 - y0 };
}

function overlapArea(a: Rect, b: Rect): number {
  const w = Math.min(a.x + a.w, b.x + b.w) - Math.max(a.x, b.x);
  const hh = Math.min(a.y + a.h, b.y + b.h) - Math.max(a.y, b.y);
  return w > 0 && hh > 0 ? w * hh : 0;
}

/**
 * Punkter langs et træk, begge ender med. Hånden glider hele vejen fra `from` til `to` og kan
 * dække et bynavn midt på vejen, selv når begge ender er fri (uitest: navne under hånden i
 * glidet på telefon-bred og desktop).
 */
export function dragPath(from: P, to: P, steps = 6): P[] {
  const pts: P[] = [];
  for (let i = 0; i <= steps; i++) {
    const t = i / steps;
    pts.push({ x: from.x + (to.x - from.x) * t, y: from.y + (to.y - from.y) * t });
  }
  return pts;
}

/** Hvor meget hånden fra `o` dækker bynavne ved målene, og om den går ud over skærmen. 0 er fri. */
export function handCost(targets: readonly P[], labels: readonly Rect[], view: { w: number; h: number }, o: HandSpot): number {
  let cost = 0;
  for (const t of targets) {
    const b = handBox(t, o);
    for (const l of labels) cost += overlapArea(b, l);
    // Uden for skærmen er lige så slemt som et dækket navn.
    if (b.x < 0 || b.y < 0 || b.x + b.w > view.w || b.y + b.h > view.h) cost += b.w * b.h;
  }
  return cost;
}

/**
 * Den første plads, hvor hånden ikke dækker et bynavn ved nogen af målene (et træk giver hele
 * vejen, se `dragPath`) og står inde på skærmen. Findes ingen, den der dækker mindst.
 */
export function handSpot(targets: readonly P[], labels: readonly Rect[], view: { w: number; h: number }): HandSpot {
  let best = HAND_SPOTS[0] as HandSpot;
  let bestCost = Infinity;
  for (const o of HAND_SPOTS) {
    const cost = handCost(targets, labels, view, o);
    if (cost === 0) return o;
    if (cost < bestCost) {
      bestCost = cost;
      best = o;
    }
  }
  return best;
}

/**
 * Hvor hånden står i et træk. Kan den glide hele vejen uden at dække et navn, gør den det. Ellers
 * hopper den (`hop`): peger på starten, forsvinder, mens det gule spor tegnes, og dukker op ved
 * målet. Det sker, når tre bynavne står tæt om en kort ledning, fx på telefon på langs.
 */
export function dragSpot(from: P, to: P, labels: readonly Rect[], view: { w: number; h: number }): HandSpot & { hop: boolean } {
  const path = dragPath(from, to);
  const glide = handSpot(path, labels, view);
  if (handCost(path, labels, view, glide) === 0) return { ...glide, hop: false };
  const ends = [from, to];
  const hop = handSpot(ends, labels, view);
  return handCost(ends, labels, view, hop) === 0 ? { ...hop, hop: true } : { ...glide, hop: false };
}

/**
 * `over`: hånden peger på en knap i UI'et (kort eller power-up) og skal ligge over den.
 * `quiet`: ingen pulserende ring, fordi ringen ellers ville dække knappens tekst.
 * `ring`: kun en pulserende turkis ring om målet, ingen hånd og intet spor (de svære sværheder).
 */
export type GuideShow = ({ mode: 'drag'; from: P; to: P } | { mode: 'tap'; at: P }) & { word?: string; over?: boolean; quiet?: boolean; ring?: boolean } | null;

// Hånden med et grønt ærme og en hvid manchet: lidt tegnefilm, lidt montørhandske.
const { green: SLEEVE, white: CUFF, ink: LINE } = BRAND;
const HAND =
  '<svg viewBox="0 0 48 72" width="48" height="72" aria-hidden="true">' +
  `<path d="M13 52.5 33.5 47l5 21.5-20.5 3z" fill="${SLEEVE}" stroke="${LINE}" stroke-width="2.6" stroke-linejoin="round"/>` +
  `<path d="M12.5 50.5 34.5 45l1.6 6.2-21.8 5.2z" fill="${CUFF}" stroke="${LINE}" stroke-width="2.4" stroke-linejoin="round"/>` +
  `<path d="M17 4.5a4.5 4.5 0 0 1 9 0V22l1.8-.6a4.4 4.4 0 0 1 5.5 2.6l.3.8 1.5-.4a4.3 4.3 0 0 1 5.3 3l.2.8 1-.2a4 4 0 0 1 4.9 3.9V40c0 8.3-6.7 15-15 15h-3.6a14 14 0 0 1-11.6-6.2L4.2 37.3a4.3 4.3 0 0 1 6.6-5.4L17 37.4z" fill="${CUFF}" stroke="${LINE}" stroke-width="2.6" stroke-linejoin="round"/>` +
  `<path d="M19.5 9.5v8" stroke="${LINE}" stroke-width="1.6" stroke-linecap="round" opacity=".35"/></svg>`;

export class Guide {
  readonly el = h('div', { class: 'guide', hidden: true, 'aria-hidden': 'true' });
  private hand = h('div', { class: 'guide-hand', html: HAND });
  private ring = h('div', { class: 'guide-ring' });
  private trail = h('div', { class: 'guide-trail' });
  private word = h('div', { class: 'guide-word', hidden: true });
  private mode: 'drag' | 'tap' | null = null;
  /** Lille jubel (stjerne og ringe), når en lektion lykkes. Ligger uden for .guide, som skjules. */
  readonly cheerEl = h('div', { class: 'guide-cheer', hidden: true, 'aria-hidden': 'true' });
  private cheerTimer = 0;

  constructor() {
    this.el.append(this.trail, this.ring, this.hand, this.word);
  }

  /** Hånden er tilfreds: en stjerne popper op, hvor det skete. */
  cheer(at: P | null, star: string): void {
    if (!at) return;
    const c = this.cheerEl;
    c.innerHTML = star;
    c.style.left = `${Math.round(at.x)}px`;
    c.style.top = `${Math.round(at.y)}px`;
    c.hidden = false;
    c.classList.remove('go');
    void c.offsetWidth;
    c.classList.add('go');
    window.clearTimeout(this.cheerTimer);
    this.cheerTimer = window.setTimeout(() => {
      c.hidden = true;
    }, 900);
  }

  /** `labels`: bynavnenes kasser på skærmen; hånden på kortet stiller sig, så den ikke dækker dem. */
  update(g: GuideShow, labels: readonly Rect[] = []): void {
    if (!g) {
      if (!this.el.hidden) this.el.hidden = true;
      this.mode = null;
      return;
    }
    this.el.hidden = false;
    const w = g.word ?? '';
    if (this.word.textContent !== w) this.word.textContent = w;
    this.word.hidden = !w;
    if (g.mode !== this.mode) {
      this.mode = g.mode;
      this.el.className = `guide ${g.mode}`;
    }
    this.el.classList.toggle('over', !!g.over);
    this.el.classList.toggle('quiet', !!g.quiet && !g.ring);
    this.el.classList.toggle('ring-only', !!g.ring);
    const st = this.el.style;
    // Peger hånden på en knap i UI'et, står fingeren lige på knappen; på kortet står den ved siden af.
    const view = { w: window.innerWidth, h: window.innerHeight };
    const o = g.over ? { x: 0, y: 0, r: 0, hop: false } : g.mode === 'drag' ? dragSpot(g.from, g.to, labels, view) : { ...handSpot([g.at], labels, view), hop: false };
    this.el.classList.toggle('hop', o.hop);
    st.setProperty('--hx', `${o.x}px`);
    st.setProperty('--hy', `${o.y}px`);
    st.setProperty('--hr', `${o.r}deg`);
    if (g.mode === 'drag') {
      st.setProperty('--ax', `${Math.round(g.from.x)}px`);
      st.setProperty('--ay', `${Math.round(g.from.y)}px`);
      st.setProperty('--bx', `${Math.round(g.to.x)}px`);
      st.setProperty('--by', `${Math.round(g.to.y)}px`);
      const dx = g.to.x - g.from.x;
      const dy = g.to.y - g.from.y;
      st.setProperty('--len', `${Math.round(Math.hypot(dx, dy))}px`);
      st.setProperty('--ang', `${Math.atan2(dy, dx)}rad`);
    } else {
      st.setProperty('--bx', `${Math.round(g.at.x)}px`);
      st.setProperty('--by', `${Math.round(g.at.y)}px`);
    }
  }
}
